package io.horion.cv.pages.dashboard

import androidx.compose.runtime.*
import io.horion.cv.components.auth.RequireAuth
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.cv.components.preview.CvPreviewPanel
import io.horion.cv.components.preview.FormContent
import io.horion.cv.di.koinGet
import io.horion.cv.models.ExtraSection
import io.horion.cv.pages.navigation.Screen
import io.horion.cv.utils.Colors
import io.horion.cv.viewModel.CvViewModel
import io.horion.cv.viewModel.DashboardViewModel
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text
import io.horion.cv.components.widgets.ToastType
import io.horion.cv.components.widgets.AlertMessage
import io.horion.cv.components.LoadingPopup
import io.horion.cv.styles.InfoDividerStyle
import io.horion.cv.styles.InfoFormScrollContainerStyle
import io.horion.cv.styles.InfoMainLayoutContainerStyle
import io.horion.cv.styles.InfoPreviewContainerStyle

private sealed class LoadState {
    data object Loading : LoadState()
    data object Loaded : LoadState()
    data object Error : LoadState()
}

@OptIn(DelicateApi::class)
@Page("update-cv")
@Composable
fun UpdateCvPage() = RequireAuth {

    val cvViewModel = koinGet<CvViewModel>()
    val dashboardViewModel = koinGet<DashboardViewModel>()
    val ctx = rememberPageContext()

    val cvId = ctx.route.queryParams["id"] ?: ""
    val cvModel = ctx.route.queryParams["model"]

    var openedSectionId by remember { mutableStateOf<String?>(null) }
    var activeExtraSections by remember { mutableStateOf<List<ExtraSection>>(emptyList()) }
    var loadState by remember { mutableStateOf<LoadState>(LoadState.Loading) }
    var showUpdateSuccess by remember { mutableStateOf(false) }
    var updateError by remember { mutableStateOf<String?>(null) }

    LoadingPopup(cvViewModel.cvUiState.loading)

    AlertMessage(
        message = "CV mis à jour avec succès",
        isVisible = showUpdateSuccess,
        type = ToastType.SUCCESS,
        duration = 1500,
        onDismiss = {
            showUpdateSuccess = false
            ctx.router.navigateTo(Screen.CvDashboard.path)
        }
    )

    AlertMessage(
        message = updateError ?: "",
        isVisible = updateError != null,
        type = ToastType.ERROR,
        onDismiss = { updateError = null }
    )

    LaunchedEffect(cvViewModel.cvUiState.error) {
        cvViewModel.cvUiState.error?.let { updateError = it }
    }

    LaunchedEffect(cvId, cvModel) {
        cvViewModel.resetToBlank()
        activeExtraSections = emptyList()
        openedSectionId = null

        loadState = if (cvId.isBlank()) {
            LoadState.Error
        } else {
            val cv = cvViewModel.loadCvForEditById(cvId, cvModel)
            if (cv != null) LoadState.Loaded else LoadState.Error
        }
    }

    fun updateSectionTitle(sectionId: String, newTitle: String) {
        activeExtraSections = activeExtraSections.map {
            if (it.id == sectionId) it.copy(title = newTitle)
            else it
        }
    }

    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark

    Style {
        """
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-8px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes popUp {
            from { opacity: 0; transform: translateY(10px) scale(0.97); }
            to { opacity: 1; transform: translateY(0) scale(1); }
        }
        """.trimIndent()
    }

    Div(
        attrs = Modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("height", "100vh")
                property("max-height", "100vh")
                property("overflow", "hidden")
                property(
                    "background-color",
                    if (isDark) {
                        Colors.DarkBG.toString()
                    } else {
                        Colors.White.toString()
                    }
                )
                property("transition", "background-color 300ms ease")
            }
            .toAttrs()
    ) {
//        Headbar(
//            cvViewModel = cvViewModel
//        )

        when (loadState) {
            is LoadState.Loading -> {
                Div(attrs = {
                    style {
                        property("flex", "1")
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                    }
                }) {
                    Text("Chargement du CV...")
                }
            }

            is LoadState.Error -> {
                Div(attrs = {
                    style {
                        property("flex", "1")
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("gap", "16px")
                    }
                }) {
                    Text("Impossible de charger ce CV.")
                    org.jetbrains.compose.web.dom.Button(attrs = {
                        onClick { ctx.router.navigateTo(Screen.CvDashboard.path) }
                        style {
                            property("cursor", "pointer")
                            property("padding", "10px 20px")
                            property("border-radius", "8px")
                        }
                    }) {
                        Text("Retour au dashboard")
                    }
                }
            }

            is LoadState.Loaded -> {
                Div(
                    attrs = InfoMainLayoutContainerStyle
                        .toModifier()
                        .toAttrs()
                ) {
                    Div(
                        attrs = InfoFormScrollContainerStyle
                            .toModifier()
                            .toAttrs()
                    ) {
                        FormContent(
                            cvViewModel = cvViewModel,
                            openedSectionId = openedSectionId,
                            activeExtraSections = activeExtraSections,
                            onSectionOpen = { openedSectionId = it },
                            onAddExtraSection = { extra ->
                                activeExtraSections = activeExtraSections + extra
                                openedSectionId = extra.id
                            },
                            onOpenExtraSection = { extraSection ->
                                openedSectionId = extraSection.id
                            },
                            onRenameSection = { id, title ->
                                updateSectionTitle(id, title)
                            },
                            submitLabel = "Modifier",
                            onCustomSubmit = {
                                cvViewModel.updateExistingCv {
                                    showUpdateSuccess = true
                                }
                            }
                        )
                    }

                    Div(
                        attrs = InfoDividerStyle
                            .toModifier()
                            .toAttrs()
                    )

                    Div(
                        attrs = InfoPreviewContainerStyle
                            .toModifier()
                            .toAttrs()
                    ) {
                        CvPreviewPanel(
                            data = cvViewModel.cvUiState.data,
                            cvViewModel = cvViewModel,
                            model = cvViewModel.cvUiState.model
                        )
                    }
                }
            }
        }
    }

//    Style {
//        """
//        @keyframes fadeIn {
//            from { opacity: 0; transform: translateY(-8px); }
//            to { opacity: 1; transform: translateY(0); }
//        }
//        @keyframes popUp {
//            from { opacity: 0; transform: translateY(10px) scale(0.97); }
//            to { opacity: 1; transform: translateY(0) scale(1); }
//        }
//        """.trimIndent()
//    }
//
//    Div(attrs = {
//        style {
//            property("display", "flex")
//            property("flex-direction", "column")
//            property("height", "100vh")
//            property("width", "100%")
//            property("overflow", "hidden")
//            property("background-color", if (isDark) Colors.DarkBG.toString() else Colors.White.toString())
//            property("transition", "background-color 300ms ease")
//        }
//    }) {
//        when (loadState) {
//            is LoadState.Loading -> {
//                Div(attrs = {
//                    style {
//                        property("flex", "1")
//                        property("display", "flex")
//                        property("align-items", "center")
//                        property("justify-content", "center")
//                    }
//                }) {
//                    Text("Chargement du CV...")
//                }
//            }
//
//            is LoadState.Error -> {
//                Div(attrs = {
//                    style {
//                        property("flex", "1")
//                        property("display", "flex")
//                        property("flex-direction", "column")
//                        property("align-items", "center")
//                        property("justify-content", "center")
//                        property("gap", "16px")
//                    }
//                }) {
//                    Text("Impossible de charger ce CV.")
//                    org.jetbrains.compose.web.dom.Button(attrs = {
//                        onClick { ctx.router.navigateTo(Screen.CvDashboard.path) }
//                        style {
//                            property("cursor", "pointer")
//                            property("padding", "10px 20px")
//                            property("border-radius", "8px")
//                        }
//                    }) {
//                        Text("Retour au dashboard")
//                    }
//                }
//            }
//
//            is LoadState.Loaded -> {
//                if (isMobile) {
//                    Div(attrs = {
//                        style {
//                            property("flex", "1")
//                            property("overflow-y", "auto")
//                            property("overflow-x", "hidden")
//                            property("padding-bottom", "50px")
//                        }
//                    }) {
//                        FormContent(
//                            cvViewModel = cvViewModel,
//                            openedSectionId = openedSectionId,
//                            activeExtraSections = activeExtraSections,
//                            onSectionOpen = { openedSectionId = it },
//                            onAddExtraSection = { extra ->
//                                activeExtraSections = activeExtraSections + extra
//                                openedSectionId = extra.id
//                            },
//                            onRenameSection = { id, title ->
//                                updateSectionTitle(id, title)
//                            },
//                            submitLabel = "Modifier",
//                            onCustomSubmit = {
//                                cvViewModel.updateExistingCv {
//                                    showUpdateSuccess = true
//                                }
//                            }
//                        )
//                    }
//                } else {
//                    Div(attrs = {
//                        style {
//                            property("display", "flex")
//                            property("flex", "1")
//                            property("min-height", "0")
//                            property("overflow", "hidden")
//                        }
//                    }) {
//                        Div(attrs = {
//                            style {
//                                property("flex", "1")
//                                property("overflow-y", "auto")
//                                property("overflow-x", "hidden")
//                                property("padding-bottom", if (isTablet) "70px" else "100px")
//                                property("background-color", if (isDark) Colors.DarkBG.toString() else Colors.White.toString())
//                            }
//                        }) {
//                            FormContent(
//                                cvViewModel = cvViewModel,
//                                openedSectionId = openedSectionId,
//                                activeExtraSections = activeExtraSections,
//                                onSectionOpen = { openedSectionId = it },
//                                onAddExtraSection = { extra ->
//                                    activeExtraSections = activeExtraSections + extra
//                                    openedSectionId = extra.id
//                                },
//                                onRenameSection = { id, title ->
//                                    updateSectionTitle(id, title)
//                                },
//                                submitLabel = "Modifier",
//                                onCustomSubmit = {
//                                    cvViewModel.updateExistingCv {
//                                        showUpdateSuccess = true
//                                    }
//                                }
//                            )
//                        }
//
//                        Div(attrs = {
//                            style {
//                                property("width", "1px")
//                                property("background-color", if (isDark) "#2a2a3e" else Colors.LightGray.toString())
//                                property("flex-shrink", "0")
//                            }
//                        })
//
//                        Div(attrs = {
//                            style {
//                                property("flex", "1")
//                                property("overflow-y", "auto")
//                                property("overflow-x", "hidden")
//                                property("background-color", Colors.LightGray.toString())
//                                property("display", "flex")
//                                property("flex-direction", "column")
//                                property("align-items", "center")
//                            }
//                        }) {
//                            CvPreviewPanel(
//                                data = cvViewModel.cvUiState.data,
//                                model = cvViewModel.cvUiState.model
//                            )
//                        }
//                    }
//                }
//            }
//        }
//    }
}